/**
 * Timezone utilities
 * Shared browser timezone detection and formatting helpers.
 */

/**
 * Sentinel stored in `user.timezone` meaning "follow whatever zone this browser
 * reports" — the sibling of the 'tenant_default'/'locale_default' sentinels on the
 * other locale preferences. It is what makes the literal 'UTC' a pinnable choice:
 * while 'UTC' doubled as the column default, a user who deliberately picked UTC was
 * indistinguishable from one who had never picked anything, so every consumer
 * silently fell back to the browser's ambient zone instead.
 */
export const BROWSER_DEFAULT_TIMEZONE = 'browser_default';

/**
 * Returns the browser's IANA timezone string (e.g., "America/New_York").
 * Falls back to 'UTC' if detection fails.
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Resolve a stored timezone preference to the concrete zone to render in.
 *
 * An explicit choice always wins — 'UTC' included. Only the sentinel (or a missing
 * value, on a row predating it) defers to the browser, and it re-reads the LIVE
 * browser zone on every call, so an "automatic" user follows their device as they
 * travel rather than being frozen at whatever zone their last login reported.
 */
export function resolveTimezonePreference(preference: string | null | undefined): string {
  if (preference && preference !== BROWSER_DEFAULT_TIMEZONE) return preference;
  return getBrowserTimezone();
}

export interface WallClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * Wall-clock parts of `date` as observed in `timeZone`.
 *
 * This is the single primitive for "what does the clock/calendar read in the
 * user's zone at this instant". Anything that needs a calendar day, a day
 * boundary, or a wall-clock time MUST resolve it through here rather than the
 * `Date` getters (`getDate()`, `setHours()`, …), which silently answer for the
 * BROWSER's ambient zone — a different zone from the user's configured one
 * whenever they travel or set their timezone explicitly.
 */
export function zonedParts(date: Date, timeZone: string): WallClockParts {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const map: Record<string, string> = {};
  for (const part of dtf.formatToParts(date)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  // Some engines emit hour '24' for midnight; normalize to '00'.
  const hour = map.hour === '24' ? '00' : map.hour;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(hour),
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

const MS_PER_DAY = 86_400_000;

/**
 * The calendar day `date` falls on in `timeZone`, as a whole number of days since
 * the epoch. Subtracting two of these yields the number of calendar days between
 * them in that zone — the DST-safe way to ask "is this today, or yesterday?".
 *
 * Deliberately day-resolution: it counts *calendar day* boundaries, not elapsed
 * 24h periods, so a DST day of 23 or 25 hours still counts as exactly one day.
 */
export function zonedDayNumber(date: Date, timeZone: string): number {
  const { year, month, day } = zonedParts(date, timeZone);
  return Date.UTC(year, month - 1, day) / MS_PER_DAY;
}

/**
 * Returns the UTC offset string for a given IANA timezone (e.g., "UTC+2", "UTC-5").
 * Returns empty string on failure.
 */
export function getUtcOffset(tz: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    const offset = tzPart?.value?.replace('GMT', 'UTC');
    if (!offset) return '';
    // Newer ICU (e.g. macOS) emits "UTC+0"/"GMT+0" for the UTC zone, while
    // Linux CI's ICU emits plain "UTC". Collapse any zero offset to "UTC" so
    // the output is stable across environments.
    if (/^UTC[+-]0+(?::0+)?$/.test(offset)) return 'UTC';
    return offset;
  } catch {
    return '';
  }
}

/**
 * Common IANA timezones grouped by region.
 * Covers ~95% of users without overwhelming the picker.
 */
const COMMON_TIMEZONES = [
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Halifax',
  'America/St_Johns',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Santiago',
  'America/Bogota',
  'America/Mexico_City',
  'Atlantic/Reykjavik',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Europe/Warsaw',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Athens',
  'Europe/Bucharest',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Asia/Dubai',
  'Asia/Jerusalem',
  'Asia/Riyadh',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'UTC',
];

export interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

/**
 * Returns a list of timezone options with labels and UTC offsets,
 * sorted by UTC offset. The browser's detected timezone is always included.
 */
export function getTimezoneOptions(): TimezoneOption[] {
  const browserTz = getBrowserTimezone();
  const tzSet = new Set(COMMON_TIMEZONES);
  if (browserTz !== 'UTC') {
    tzSet.add(browserTz);
  }

  const options = Array.from(tzSet).map((tz) => {
    const offset = getUtcOffset(tz);
    const city = tz.replace(/^.*\//, '').replace(/_/g, ' ');
    const region = tz.split('/')[0];
    const label = tz === 'UTC' ? 'UTC' : `${city} (${offset}) — ${region}`;
    return { value: tz, label, offset };
  });

  // Sort by numeric UTC offset
  options.sort((a, b) => {
    const parseOffset = (o: string) => {
      if (!o || o === 'UTC') return 0;
      const match = o.match(/UTC([+-])(\d+)(?::(\d+))?/);
      if (!match) return 0;
      const sign = match[1] === '+' ? 1 : -1;
      return sign * (parseInt(match[2]) * 60 + parseInt(match[3] || '0'));
    };
    return parseOffset(a.offset) - parseOffset(b.offset);
  });

  return options;
}
